import Swal from 'sweetalert2'
import { escapeHtml } from './escapeHtml'

/**
 * The readable text of an error value: a string as is, a list item by item, an object by its `message` (the shape of
 * a list of field errors) or else by its string leaves.
 */
const asText = (value: unknown): string => {
  if (typeof value === 'string') {
    return value
  }
  if (Array.isArray(value)) {
    return value.map(asText).filter((item) => item !== '').join(', ')
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  if (value && typeof value === 'object') {
    const message = (value as { message?: unknown }).message
    if (typeof message === 'string' && message !== '') {
      return message
    }

    return Object.values(value as Record<string, unknown>)
      .filter((item) => typeof item === 'string' || Array.isArray(item) || (item !== null && typeof item === 'object'))
      .map(asText)
      .filter((item) => item !== '')
      .join(', ')
  }

  return ''
}

/**
 * Lines of the 400 alert, as escaped HTML: the backend's sentence alone when the body carries one (other keys next to
 * it are context data, not field errors); otherwise one line per field error, named by its field (a nested map by its
 * string leaves); for a list body, the text of its items (an object item by its `message`); a string body as it is.
 */
const errorLines = (errors: unknown): string[] => {
  if (typeof errors === 'string') {
    return errors.trim() === '' ? [] : [`<br/ ><span>${escapeHtml(errors)}</span>`]
  }

  if (Array.isArray(errors)) {
    return errors
      .map(asText)
      .filter((text) => text !== '')
      .map((text) => `<br/ ><span>${escapeHtml(text)}</span>`)
  }

  if (!errors || typeof errors !== 'object') {
    return []
  }

  const generalError = asText((errors as Record<string, unknown>).general_error)
  if (generalError !== '') {
    return [`<br/ ><span>${escapeHtml(generalError)}</span>`]
  }

  return Object.entries(errors as Record<string, unknown>)
    .map(([key, value]) => [key, asText(value)])
    .filter(([, text]) => text !== '')
    .map(([key, text]) => `<br/ ><span><strong>${escapeHtml(key)}</strong>: ${escapeHtml(text)}</span>`)
}

/** Whether a 400 body has anything the common alert can show (`errors: []` and `errors: {}` have not). */
export const hasErrorText = (errors: unknown): boolean => errorLines(errors).length > 0

export const handleAPIErrors = (errors: unknown, status: number) => {
  if (status !== 200) {
    const lines = errorLines(errors)

    if (lines.length) {
      Swal.fire({ title: 'Ошибка', html: `Произошла ошибка: ${lines.join('')}`, icon: 'error' })
    }

    return true
  }
}
